import type { PDFPageProxy } from 'pdfjs-dist/types/src/display/api'
import type { DoodleLayer, DoodleLayerId, DoodleOverlay } from '../../../server/src/models'
import { FakeImmutableMap } from '../FakeImmutableMap'
import { i18n } from '../i18n'
import { InsufficientCanvasMemoryError, loadPdfDocument } from '../pdf'
import { canvasToBlob, createCanvas, isMobileSafari, loadImageBlob } from '../utils'
import { DoodleShape } from './DoodleShape'

// 参考: https://github.com/jhildenbiddle/canvas-size/blob/master/src/test-sizes.js
const isPossibleCanvasSize = (width: number, height: number) =>
  isMobileSafari ? width * height <= 16_777_216 : width <= 65535 && height <= 65535 && width * height <= 16384 * 16384

export interface DoodleDocument {
  readonly resolutionScale: number
  readonly totalPageCount: number
  getPage(oneBasedPageNumber: number): WritableDoodlePageContent
  withReset(): DoodleDocument
}

export class LayeredShapes {
  constructor(
    shapeIdShapeMap?: ReadonlyMap<string, DoodleShape>,
    layerIdShapeIdShapeMap?: ReadonlyMap<DoodleLayerId, FakeImmutableMap<string, DoodleShape>>,
  ) {
    this.#shapeIdShapeMap = new Map(shapeIdShapeMap)
    this.#layerIdShapeIdShapeMap = new Map(layerIdShapeIdShapeMap)
  }

  readonly #shapeIdShapeMap = new Map<string, DoodleShape>()
  readonly #layerIdShapeIdShapeMap = new Map<DoodleLayerId, FakeImmutableMap<string, DoodleShape>>()

  clone() {
    return new LayeredShapes(this.#shapeIdShapeMap, this.#layerIdShapeIdShapeMap)
  }

  findShape(shapeId: string): DoodleShape | undefined {
    return this.#shapeIdShapeMap.get(shapeId)
  }

  getShapesByLayerId(layerId: DoodleLayerId): ReadonlyMap<string, DoodleShape> | undefined {
    return this.#layerIdShapeIdShapeMap.get(layerId)
  }

  #updateLayerIdShapeIdShapeMap(
    layerId: string,
    update: (map: FakeImmutableMap<string, DoodleShape>) => FakeImmutableMap<string, DoodleShape>,
  ) {
    this.#layerIdShapeIdShapeMap.set(layerId, update(this.#layerIdShapeIdShapeMap.get(layerId) ?? new FakeImmutableMap()))
  }

  addShape(shape: DoodleShape) {
    this.#shapeIdShapeMap.set(shape.properties.id, shape)
    this.#updateLayerIdShapeIdShapeMap(shape.properties.layerId, map => map.set(shape.properties.id, shape))
  }

  updateShape(shape: DoodleShape) {
    const old = this.#shapeIdShapeMap.get(shape.properties.id)
    if (old && old.properties.layerId !== shape.properties.layerId) {
      this.#updateLayerIdShapeIdShapeMap(old.properties.layerId, map => map.delete(old.properties.id))
    }
    this.addShape(shape)
  }

  updateShapes(update: (shape: DoodleShape) => DoodleShape) {
    for (const [layerId, layerShapeMap] of this.#layerIdShapeIdShapeMap.entries()) {
      this.#layerIdShapeIdShapeMap.set(
        layerId,
        layerShapeMap.updateValues(shape => {
          const updatedShape = update(shape)
          if (updatedShape !== shape) {
            this.#shapeIdShapeMap.set(shape.properties.id, updatedShape)
          }
          return updatedShape
        }),
      )
    }
  }

  deleteShapeById(shapeId: string) {
    const shape = this.#shapeIdShapeMap.get(shapeId)
    if (shape) {
      this.#updateLayerIdShapeIdShapeMap(shape.properties.layerId, map => map.delete(shape.properties.id))
      this.#shapeIdShapeMap.delete(shapeId)
    }
  }
}

export interface DoodlePageContent {
  readonly width: number
  readonly height: number
  readonly backgroundRotation: number
  readonly layers: readonly DoodleLayer[]
  readonly shapes: LayeredShapes
  readonly comments: ReadonlyMap<string, DoodleComment>
  readonly resolutionScale: number
  readonly overlays?: readonly DoodleOverlay[]
  prerender?(): void | Promise<void>
  render(noCache?: boolean): DoodleImageElement | Promise<DoodleImageElement>
}
export interface WritableDoodlePageContent extends DoodlePageContent {
  backgroundRotation: number
  layers: readonly DoodleLayer[]
  readonly shapes: LayeredShapes
  readonly comments: Map<string, DoodleComment>
  overlays?: readonly DoodleOverlay[]
}

type DoodleImageElement = HTMLImageElement

export interface DoodleCommentReply {
  readonly comment: string
  readonly uid: string
  readonly createdAt: number
}

export interface DoodleComment {
  readonly id: string
  readonly shapeId: string
  readonly comment: string
  readonly uid: string
  readonly createdAt: number
  readonly replies: Map<string, DoodleCommentReply>
}

interface DoodleCommentLike {
  readonly shapeId: string
  readonly createdAt?: number
}

const defaultLayers = (): readonly DoodleLayer[] => [
  { id: 'drawing', name: i18n.originalDrawing, visible: true, type: 'drawing' },
  { id: '', name: 'Layer 1', visible: true },
]

const canvasToImage = async (canvas: HTMLCanvasElement, width: number, height: number) => {
  const image = new Image(width, height)
  const blob = await canvasToBlob(canvas, 'image/png')
  await new Promise((resolve, reject) => {
    image.src = URL.createObjectURL(blob)
    image.onload = resolve
    image.onerror = reject
    image.onabort = reject
  })
  URL.revokeObjectURL(image.src)
  return image
}

const compareNumber = (a: number | undefined, b: number | undefined) => (a && b ? a - b : a ? -1 : 1)
export const compareCommentsByShapeCoordinate = (shapes: LayeredShapes) => (a: DoodleCommentLike, b: DoodleCommentLike) => {
  if (a.shapeId === b.shapeId) {
    return compareNumber(a.createdAt, b.createdAt)
  }
  const aBBox = shapes.findShape(a.shapeId)?.bbox
  const bBBox = shapes.findShape(b.shapeId)?.bbox
  if (aBBox && bBBox) {
    return aBBox.y - bBBox.y || aBBox.x - bBBox.x || compareNumber(a.createdAt, b.createdAt)
  } else {
    return (!bBBox as boolean & number) - (!aBBox as boolean & number)
  }
}

class DoodlePageContentBase {
  readonly shapes = new LayeredShapes()
  readonly comments = new Map<string, DoodleComment>()

  private _layers = defaultLayers()
  private _layersWithOverlays: readonly DoodleLayer[] | undefined
  get layers() {
    return (this._layersWithOverlays ??= this._createLayersWithOverlays())
  }
  set layers(layers: readonly DoodleLayer[]) {
    this._layers = layers
    this._layersWithOverlays = undefined
  }

  _overlays: readonly DoodleOverlay[] | undefined
  get overlays() {
    return this._overlays
  }
  set overlays(overlays: readonly DoodleOverlay[] | undefined) {
    this._overlays = overlays?.length ? overlays : undefined
    this._layersWithOverlays = undefined
  }

  _createLayersWithOverlays(): readonly DoodleLayer[] {
    if (this._overlays === undefined) {
      return this._layers
    }
    const additionalLayers = this._overlays
      .filter(overlay => !this._layers.some(l => l.type === 'overlay' && l.id === overlay.layerId))
      .map<DoodleLayer>(overlay => ({
        id: overlay.layerId,
        name: overlay.app?.toUpperCase() ?? i18n.externalApp,
        visible: true,
        type: 'overlay',
      }))
    return additionalLayers.length ? [...this._layers, ...additionalLayers] : this._layers
  }
}

class DoodleImageDocument extends DoodlePageContentBase implements DoodleDocument, DoodlePageContent {
  readonly resolutionScale = 1
  readonly totalPageCount = 1

  _width: number
  _height: number
  _backgroundRotation = 0
  _rendered: { readonly backgroundRotation: number; readonly image$: Promise<DoodleImageElement> } | undefined

  get width() {
    return this._width * this.scale
  }
  get height() {
    return this._height * this.scale
  }
  get backgroundRotation() {
    return this._backgroundRotation
  }
  set backgroundRotation(backgroundRotation) {
    this._backgroundRotation = backgroundRotation
    this._width = backgroundRotation % 180 ? this.originalImage.height : this.originalImage.width
    this._height = backgroundRotation % 180 ? this.originalImage.width : this.originalImage.height
  }

  constructor(
    private readonly originalImage: HTMLImageElement | ImageBitmap,
    private readonly scale: number,
  ) {
    super()
    this._width = this.originalImage.width
    this._height = this.originalImage.height
  }

  getPage() {
    return this
  }

  render() {
    const { _width: width, _height: height, backgroundRotation, originalImage, scale } = this
    if (this._rendered?.backgroundRotation === backgroundRotation) {
      return this._rendered.image$
    }
    const canvas = createCanvas({ width, height })
    const ctx = canvas.getContext('2d')!
    ctx.clearRect(0, 0, width, height)
    ctx.translate(width / 2, height / 2)
    ctx.rotate((backgroundRotation / 180) * Math.PI)
    ctx.drawImage(originalImage, -originalImage.width / 2, -originalImage.height / 2)
    const image$ = canvasToImage(canvas, width, height)
    void image$.then(image => {
      image.width = image.naturalWidth * scale
      image.height = image.naturalHeight * scale
      canvas.width = canvas.height = 0
    })
    this._rendered = { backgroundRotation, image$ }
    return image$
  }

  withReset() {
    return new DoodleImageDocument(this.originalImage, this.scale)
  }
}

const searchParams = new URLSearchParams(location.search)
const renderCacheSize = +searchParams.get('render-cache-size')! || 20
const noPrerender = renderCacheSize < 8

const cacheRendering = ((): ((page: DoodlePdfPageContent, render: () => Promise<DoodleImageElement>) => Promise<DoodleImageElement>) => {
  const cache = new Map<PDFPageProxy, { readonly rotation: number; readonly canvasPromise: Promise<DoodleImageElement> }>()
  const deleteCache = (key: PDFPageProxy) => {
    const cached = cache.get(key)
    if (cached) {
      cache.delete(key)
      return cached.canvasPromise.then(c => (c.width = c.height = 0))
    }
  }

  const renderWithRetry = async (page: DoodlePdfPageContent, render: () => Promise<DoodleImageElement>): Promise<DoodleImageElement> => {
    try {
      return await render()
    } catch (error) {
      if (error instanceof InsufficientCanvasMemoryError && cache.size > 3) {
        console.warn('insufficient canvas memory.')
        for (const key of [...cache.keys()].slice(0, Math.floor(cache.size / 2))) {
          await deleteCache(key)
        }
        return await renderWithRetry(page, render)
      } else {
        throw new InsufficientCanvasMemoryError()
      }
    }
  }

  return (page, get) => {
    const key = page.pdfPage
    const rotation = page.backgroundRotation
    const cached = cache.get(key)
    if (cached) {
      if (cached.rotation === rotation) {
        cache.delete(key)
        cache.set(key, cached)
        return cached.canvasPromise
      } else {
        void deleteCache(key)
      }
    }
    if (cache.size >= renderCacheSize) {
      const key = cache.keys().next()
      key.done || void deleteCache(key.value)
    }
    const canvasPromise = renderWithRetry(page, get)
    cache.set(key, { rotation, canvasPromise })
    return canvasPromise
  }
})()

interface DoodlePdfPageOptions {
  readonly resolutionScale: number
  readonly longerSideLength?: number
}

class DoodlePdfPageContent extends DoodlePageContentBase implements WritableDoodlePageContent {
  private _width: number
  private _height: number
  private _backgroundRotation: number
  readonly resolutionScale: number

  get width() {
    return this._width
  }
  get height() {
    return this._height
  }
  get backgroundRotation() {
    return this._backgroundRotation
  }
  set backgroundRotation(backgroundRotation) {
    // 回転角が設定されている PDF で、たとえば初期回転角 90° と 4 回「回転」ボタンを押した -270° とを同一視するため、角度を正規化します。
    this._backgroundRotation = (backgroundRotation + 360) % 360
    const viewport = this.pdfPage.getViewport({ scale: 1, rotation: backgroundRotation })
    this._width = viewport.width
    this._height = viewport.height
  }
  get pageNumber() {
    return this.pdfPage.pageNumber
  }

  constructor(
    readonly pdfPage: PDFPageProxy,
    options: DoodlePdfPageOptions,
  ) {
    super()
    const viewport = this.pdfPage.getViewport({ scale: 1 })
    if (options?.longerSideLength) {
      if (viewport.width < viewport.height) {
        this.resolutionScale = options.longerSideLength / viewport.height
        this._height = options.longerSideLength
        this._width = viewport.width * this.resolutionScale
      } else {
        this.resolutionScale = options.longerSideLength / viewport.width
        this._width = options.longerSideLength
        this._height = viewport.height * this.resolutionScale
      }
    } else {
      this._width = viewport.width
      this._height = viewport.height
      this.resolutionScale = options.resolutionScale
      while (!isPossibleCanvasSize(viewport.width * this.resolutionScale, viewport.height * this.resolutionScale)) {
        this.resolutionScale = this.resolutionScale / 2
      }
    }
    this._backgroundRotation = this.pdfPage.rotate
  }

  async prerender() {
    noPrerender || (await this.render())
  }

  render(noCache?: boolean) {
    return noCache ? this._render() : cacheRendering(this, () => this._render())
  }

  private async _render() {
    const { pdfPage, backgroundRotation: rotation } = this
    const viewport = pdfPage.getViewport({ scale: this.resolutionScale, rotation })

    // Safari は Canvas 全体で割り当てるメモリに制限があるようで、 Canvas 用のメモリが足りないときは単に getContext() が null を返します。
    // https://stackoverflow.com/questions/40482586/getcontext2d-returns-null-in-safari-10
    // https://qiita.com/minimo/items/b724c6793f45aca5e6f5
    const canvas = createCanvas(viewport)
    const canvasContext = canvas.getContext('2d', { alpha: false })
    if (!canvasContext) {
      throw new InsufficientCanvasMemoryError()
    }
    await this.pdfPage.render({ canvas, canvasContext, viewport, intent: 'print' }).promise
    // canvas.style.width = `${this.width}px`
    // canvas.style.height = `${this.height}px`
    const image = await canvasToImage(canvas, this.width, this.height)
    canvas.width = canvas.height = 0
    return image
  }
}

class DoodlePdfDocument implements DoodleDocument {
  static async load(pdfBuffer: ArrayBuffer, options?: DoodlePdfPageOptions) {
    const pdfDocument = await loadPdfDocument(new Uint8Array(pdfBuffer))
    const pages: PDFPageProxy[] = []
    for (let i = 1; i <= pdfDocument.numPages; i++) {
      pages.push(await pdfDocument.getPage(i))
    }
    return new DoodlePdfDocument(pages, {
      longerSideLength: options?.longerSideLength,
      resolutionScale: options?.longerSideLength ? 0 : options?.resolutionScale || (isMobileSafari ? 2 : 4),
    })
  }

  readonly totalPageCount: number
  private readonly pageContentsMap: Record<number, DoodlePdfPageContent | undefined> = {}

  get resolutionScale(): number {
    return this.options.resolutionScale
  }

  constructor(
    readonly pdfPages: readonly PDFPageProxy[],
    private readonly options: DoodlePdfPageOptions,
  ) {
    this.totalPageCount = this.pdfPages.length
  }

  getPage(pageNumber: number) {
    return (
      this.pageContentsMap[pageNumber] ??
      (this.pageContentsMap[pageNumber] = new DoodlePdfPageContent(this.pdfPages[pageNumber - 1], this.options))
    )
  }

  withReset() {
    return new DoodlePdfDocument(this.pdfPages, this.options)
  }
}

export const createDoodleDocument = async (blob: Blob, options?: { pdfLongerSideLength?: number; imageScale?: number }) => {
  if (blob.type === 'application/pdf') {
    const urlSearchParams = new URLSearchParams(location.search)
    const resolutionScale = +urlSearchParams.get('resolution-scale')!
    return await DoodlePdfDocument.load(await blob.arrayBuffer(), { resolutionScale, longerSideLength: options?.pdfLongerSideLength })
  }
  if (blob.type.startsWith('image')) {
    return new DoodleImageDocument(await loadImageBlob(blob), options?.imageScale ?? 1)
  }
}
